import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Home,
  Groups,
  QrCodeScanner,
  Menu as MenuIcon,
  Assessment
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../context/AuthContext';
import { pageVariants, pageTransition } from '../theme/motion';
import { GRADIENTS, RADIUS_LIBRARIAN as RADIUS } from '../theme/tokens';
import { checkPortalAccess } from '../utils/roleRedirect';
import DesktopNav from '../components/DesktopNav';
import InstallAppButton from '../components/InstallAppButton';

// Import tab components
import LibrarianHome from './librarian/LibrarianHome';
import LibrarianSessions from './librarian/LibrarianSessions';
import LibrarianScanner from './librarian/LibrarianScanner';
import LibrarianManage from './librarian/LibrarianManage';
import LibrarianAnalytics from './librarian/LibrarianAnalytics';

const MotionBox = motion(Box);

/**
 * Librarian Portal - Mobile-First Interface
 * Features bottom navigation and mobile-optimized views
 */
function LibrarianPortal() {
  const { isAuthenticated, user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  // Determine initial tab based on route
  const getInitialTab = () => {
    const path = location.pathname;
    if (path.includes('/sessions')) return 1;
    if (path.includes('/scanner')) return 2;
    if (path.includes('/analytics')) return 3;
    if (path.includes('/manage')) return 4;
    return 0; // Home
  };

  const [currentTab, setCurrentTab] = useState(getInitialTab());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Sync tab with URL changes (for navigation from quick actions)
  useEffect(() => {
    const newTab = getInitialTab();
    if (newTab !== currentTab) {
      setCurrentTab(newTab);
    }
  }, [location.pathname]);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);

    // Update URL using React Router navigate
    const routes = [
      '/librarian',
      '/librarian/sessions',
      '/librarian/scanner',
      '/librarian/analytics',
      '/librarian/manage'
    ];
    navigate(routes[newValue], { replace: true });
  };

  const renderTabContent = () => {
    switch (currentTab) {
      case 0:
        return <LibrarianHome showSnackbar={showSnackbar} />;
      case 1:
        return <LibrarianSessions showSnackbar={showSnackbar} />;
      case 2:
        return <LibrarianScanner showSnackbar={showSnackbar} />;
      case 3:
        return <LibrarianAnalytics showSnackbar={showSnackbar} />;
      case 4:
        return <LibrarianManage showSnackbar={showSnackbar} />;
      default:
        return <LibrarianHome showSnackbar={showSnackbar} />;
    }
  };

  // Role-based access control - Only librarians can access this portal
  if (!checkPortalAccess(user, 'librarian', navigate)) {
    return null;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent' }}>
      {/* Top Navigation */}
      <DesktopNav />

      <Box
        sx={{
          minHeight: 'calc(100vh - 64px)',
          pb: '70px',
          position: 'relative',
        }}
      >
        {/* PWA Install CTA — self-hides when already installed as standalone */}
        <Box sx={{ px: 2, pt: 2 }}>
          <InstallAppButton />
        </Box>

        {/* Main Content Area */}
        <AnimatePresence mode="wait">
        <MotionBox
          key={currentTab}
          variants={pageVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={pageTransition}
        >
          {renderTabContent()}
        </MotionBox>
      </AnimatePresence>

      {/* Bottom Navigation */}
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          borderTopLeftRadius: RADIUS.xl,
          borderTopRightRadius: RADIUS.xl,
          overflow: 'hidden',
          background: GRADIENTS.brand
        }}
      >
        <BottomNavigation
          value={currentTab}
          onChange={handleTabChange}
          showLabels
          sx={{
            background: 'transparent',
            height: 70,
            '& .MuiBottomNavigationAction-root': {
              color: 'rgba(255, 255, 255, 0.6)',
              minWidth: 'auto',
              padding: '6px 12px',
              transition: 'all 0.3s ease',
              '&:hover': {
                color: 'rgba(255, 255, 255, 0.9)',
              },
              '&.Mui-selected': {
                color: 'white',
                fontSize: '0.75rem',
                paddingTop: '6px',
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.75rem',
                  fontWeight: 700
                },
                '& .MuiSvgIcon-root': {
                  transform: 'scale(1.2)',
                  filter: 'drop-shadow(0 2px 8px rgba(255, 255, 255, 0.3))'
                }
              }
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.7rem',
              fontWeight: 600,
              marginTop: '4px'
            }
          }}
        >
          <BottomNavigationAction
            label="Home"
            icon={<Home />}
          />
          <BottomNavigationAction
            label="Sessions"
            icon={<Groups />}
          />
          <BottomNavigationAction
            label="Scanner"
            icon={<QrCodeScanner />}
          />
          <BottomNavigationAction
            label="Analytics"
            icon={<Assessment />}
          />
          <BottomNavigationAction
            label="Profile"
            icon={<MenuIcon />}
          />
        </BottomNavigation>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: RADIUS.lg }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </Box>
  );
}

export default LibrarianPortal;
